import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { useRouter } from 'expo-router';

import { DocumentTitle } from '@/components/layout/DocumentTitle';
import { LlmControlCatalogue } from '@/components/settings/LlmControlCatalogue.web';
import {
  LlmControlProfiles,
  type LlmProfileChoice,
} from '@/components/settings/LlmControlProfiles.web';
import { LlmControlUsage } from '@/components/settings/LlmControlUsage.web';
import { Text } from '@/components/ui';
import {
  assignAdminLlmProfile,
  createAdminLlmModel,
  deleteAdminLlmModel,
  fetchAdminLlmCatalog,
  fetchAdminLlmModels,
  lookupAdminLlmModel,
  patchAdminLlmModel,
  refreshAdminLlmModelMetadata,
  type AdminLlmModel,
  type AdminLlmProfile,
  type LlmCatalogResponse,
  type OpenRouterCatalogMetadata,
} from '@/lib/admin/llm-models-api';

const SECTIONS = [
  { id: 'profiles', label: 'Profiles' },
  { id: 'catalogue', label: 'Catalogue' },
  { id: 'usage', label: 'Usage' },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

export function LlmModelsAdminPage() {
  const router = useRouter();
  const [section, setSection] = useState<SectionId>('profiles');
  const [models, setModels] = useState<AdminLlmModel[]>([]);
  const [profiles, setProfiles] = useState<AdminLlmProfile[]>([]);
  const [catalog, setCatalog] = useState<LlmCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [usingKey, setUsingKey] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupPreview, setLookupPreview] = useState<OpenRouterCatalogMetadata | null>(null);
  const [openrouterModelId, setOpenrouterModelId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const lookupSeq = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminLlmModels();
      setModels(data.models);
      setProfiles(data.profiles);
    } catch (err) {
      setModels([]);
      setProfiles([]);
      setError(err instanceof Error ? err.message : 'Failed to load models');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      setCatalog(await fetchAdminLlmCatalog());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load catalogue');
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadCatalog();
  }, [load, loadCatalog]);

  useEffect(() => {
    const id = openrouterModelId.trim();
    if (!id || !id.includes('/')) {
      setLookupPreview(null);
      setLookupError(null);
      setLookupLoading(false);
      return;
    }

    const seq = ++lookupSeq.current;
    setLookupLoading(true);
    setLookupError(null);

    const timer = window.setTimeout(() => {
      void lookupAdminLlmModel(id)
        .then((metadata) => {
          if (lookupSeq.current !== seq) return;
          setLookupPreview(metadata);
          setDisplayName((current) => (current.trim() ? current : metadata.model.name));
        })
        .catch((err) => {
          if (lookupSeq.current !== seq) return;
          setLookupPreview(null);
          setLookupError(err instanceof Error ? err.message : 'Lookup failed');
        })
        .finally(() => {
          if (lookupSeq.current === seq) setLookupLoading(false);
        });
    }, 400);

    return () => window.clearTimeout(timer);
  }, [openrouterModelId]);

  async function onUseForProfile(choice: LlmProfileChoice) {
    if (choice.privacy?.securityTier === 'unsafe') {
      setError('This model has no zero-data-retention route and cannot be used.');
      return;
    }
    if (
      choice.privacy?.securityTier === 'caution' &&
      !window.confirm(
        'Some routes for this model may train on or retain prompts. Couplespace will still only call a zero-retention route.\n\nContinue?',
      )
    ) {
      return;
    }

    const key = `${choice.profile}:${choice.modelId}`;
    setUsingKey(key);
    setError(null);
    try {
      let id = choice.allowlistId;
      if (!id) {
        const created = await createAdminLlmModel({
          openrouterModelId: choice.modelId,
          displayName: choice.name,
          enabled: true,
        });
        id = created.model.id;
      } else if (!choice.enabled) {
        await patchAdminLlmModel(id, { enabled: true });
      }
      setProfiles(await assignAdminLlmProfile(choice.profile, id));
      await load();
      await loadCatalog();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign profile');
    } finally {
      setUsingKey(null);
    }
  }

  async function onProviderTag(model: AdminLlmModel, tag: string | null) {
    setError(null);
    try {
      await patchAdminLlmModel(model.id, { providerTag: tag });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set privacy route');
    }
  }

  async function onAddModel() {
    const id = openrouterModelId.trim();
    if (!id) return;

    if (lookupPreview?.privacy.securityTier === 'unsafe') {
      setError('This model has no ZDR routes and cannot be enabled for chat.');
      return;
    }

    if (
      lookupPreview?.privacy.securityTier === 'caution' &&
      !window.confirm(
        'This model has privacy caveats. Chat still uses ZDR routing, but review warnings before enabling.\n\nContinue?',
      )
    ) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await createAdminLlmModel({
        openrouterModelId: id,
        displayName: displayName.trim() || undefined,
        enabled: lookupPreview?.privacy.hasZdrEndpoint ?? true,
      });
      setOpenrouterModelId('');
      setDisplayName('');
      setLookupPreview(null);
      setLookupError(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add model');
    } finally {
      setSaving(false);
    }
  }

  async function onToggleEnabled(model: AdminLlmModel) {
    const enabling = !model.enabled;
    const tier = model.openrouterMetadata?.privacy.securityTier;
    if (enabling && tier === 'unsafe') {
      setError('This model has no ZDR routes and cannot be enabled.');
      return;
    }
    if (
      enabling &&
      tier === 'caution' &&
      !window.confirm('Review privacy warnings before enabling this model. Continue?')
    ) {
      return;
    }
    setError(null);
    try {
      await patchAdminLlmModel(model.id, { enabled: enabling });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update model');
    }
  }

  async function onRefreshMetadata(model: AdminLlmModel) {
    setError(null);
    try {
      await refreshAdminLlmModelMetadata(model.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh metadata');
    }
  }

  async function onRename(model: AdminLlmModel) {
    const next = window.prompt('Display name', model.displayName);
    if (next === null) return;
    setError(null);
    try {
      await patchAdminLlmModel(model.id, { displayName: next.trim() || null });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename model');
    }
  }

  async function onDelete(model: AdminLlmModel) {
    if (!window.confirm(`Delete "${model.displayName}"?`)) return;
    setError(null);
    try {
      await deleteAdminLlmModel(model.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete model');
    }
  }

  return (
    <View className="flex-1 bg-surface px-page py-page-y">
      <DocumentTitle title="LLM control center" />
      <Pressable className="mb-4 self-start" onPress={() => router.push('/settings')}>
        <Text variant="caption" className="text-muted">
          ← Settings
        </Text>
      </Pressable>

      <Text variant="h1">LLM control center</Text>
      <View className="mt-4 rounded-lg border border-border bg-surface-raised p-4">
        <Text variant="h3">Data retention</Text>
        <Text variant="body-sm" className="mt-2">
          Chat and finance import only go to zero-data-retention routes. OpenRouter is told to deny
          data collection, so those providers do not train on prompts and do not keep them.
        </Text>
        <Text variant="body-sm" className="mt-2">
          On Catalogue, each model lists its routes. Leave it on any zero-retention route, or pin
          one privacy-friendly provider. Routes that train or retain data stay visible and are never
          selected.
        </Text>
      </View>

      <View className="mt-6 flex-row gap-4">
        {SECTIONS.map((item) => (
          <Pressable key={item.id} onPress={() => setSection(item.id)}>
            <Text variant="body" className={section === item.id ? 'text-accent' : 'text-muted'}>
              {item.label}
            </Text>
          </Pressable>
        ))}
      </View>

      {error ? (
        <Text variant="body-sm" className="mt-4 text-danger">
          {error}
        </Text>
      ) : null}

      {loading && section !== 'usage' ? <ActivityIndicator className="mt-8" /> : null}

      {!loading && section === 'profiles' ? (
        <LlmControlProfiles
          models={models}
          profiles={profiles}
          catalog={catalog}
          usingKey={usingKey}
          onUse={(choice) => void onUseForProfile(choice)}
          onChange={setProfiles}
          onError={setError}
        />
      ) : null}

      {section === 'catalogue' ? (
        <LlmControlCatalogue
          models={models}
          profiles={profiles}
          catalog={catalog}
          catalogLoading={catalogLoading}
          usingKey={usingKey}
          onCatalog={(next) => {
            setCatalog(next);
            void load();
          }}
          onUse={(choice) => void onUseForProfile(choice)}
          onProviderTag={(model, tag) => void onProviderTag(model, tag)}
          onError={setError}
          openrouterModelId={openrouterModelId}
          displayName={displayName}
          lookupLoading={lookupLoading}
          lookupError={lookupError}
          lookupPreview={lookupPreview}
          saving={saving}
          onOpenrouterModelId={setOpenrouterModelId}
          onDisplayName={setDisplayName}
          onAddModel={() => void onAddModel()}
          onToggleEnabled={(model) => void onToggleEnabled(model)}
          onRefreshMetadata={(model) => void onRefreshMetadata(model)}
          onRename={(model) => void onRename(model)}
          onDelete={(model) => void onDelete(model)}
        />
      ) : null}

      {section === 'usage' ? <LlmControlUsage models={models} onError={setError} /> : null}
    </View>
  );
}
